import { useCallback, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Button, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import {
  MaximalCardEventTags,
  SearchResultCardContactClickEvent,
  SearchResultCardViewProfileClickEvent,
} from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';

import AddToShortlistButton from '../../shortlist/components/AddToShortlistButton';
import type { FlyingAvatarRect } from '../../shortlist/components/FlyingAvatar';
import FlyingAvatar from '../../shortlist/components/FlyingAvatar';
import useShortlist from '../../shortlist/hooks/useShortlist';
import { useShortlistPreviewTarget } from '../../shortlist/ShortlistPreviewTargetContext';

import SimplifiedPriceColumn from './PriceColumn/SimplifiedPriceColumn';
import { SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';

export type Props = {
  searchResult: CommonSearchResult;
  locale: Locale;
  showFromText: boolean;
  isBHUser: boolean;
  shouldShowPriceInCredits: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  profileUrl: string;
  analyticsProps: MaximalCardEventTags;
  isDesktop?: boolean;
  isSeoPage?: boolean;
  isV2?: boolean;
};

const MaximalCTABox = ({
  searchResult: {
    avatarUrl,
    avatarSmallUrl,
    currencyCode,
    opk,
    price,
    priceInCredits,
    unitAsSentence,
    cancellationPolicy,
  },
  locale,
  showFromText,
  isBHUser,
  shouldShowPriceInCredits,
  priceTransparencyLevel,
  profileUrl,
  analyticsProps,
  isDesktop,
  isSeoPage,
  isV2,
}: Props): JSX.Element => {
  const contactUrl = `${profileUrl.replace(
    /\/\?/,
    '/contact/?'
  )}&initiation_source=search_card_contact`;
  const theme = useTheme();
  const hrefTarget = isDesktop && !isSeoPage ? '_blank' : '_self';

  const prefersReducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)');
  const { isVisible: isShortlistVisible } = useShortlist();
  const { getTargetRect: getShortlistPreviewTargetRect } = useShortlistPreviewTarget();
  const showAddToShortlist = isShortlistVisible && !isSeoPage;

  const shortlistButtonRef = useRef<HTMLElement>(null);
  const [avatarFlight, setAvatarFlight] = useState<{
    imageUrl: string;
    sourceRect: FlyingAvatarRect;
    targetRect: FlyingAvatarRect;
  } | null>(null);

  const handleShortlistAdded = useCallback(() => {
    const anchor = shortlistButtonRef.current;
    const targetRect = getShortlistPreviewTargetRect();
    const flyImage = avatarUrl || avatarSmallUrl;
    if (prefersReducedMotion || !anchor || !targetRect || !flyImage) {
      return;
    }
    setAvatarFlight({
      imageUrl: flyImage,
      sourceRect: anchor.getBoundingClientRect(),
      targetRect,
    });
  }, [prefersReducedMotion, avatarUrl, avatarSmallUrl, getShortlistPreviewTargetRect]);

  return (
    <Flex
      flexDirection="column"
      minHeight={isV2 ? undefined : '32x'}
      width="100%"
      backgroundColor={isV2 ? undefined : theme.background.accent_secondary}
      justifyContent="space-between"
      p="4x"
      gap="4x"
      sx={{ borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', marginTop: 'auto' }}
    >
      {/* V2 hides the price + cancellation policy here; the price is relocated in a follow-up PR. */}
      {!isV2 && (
        <Flex flexDirection="column" gap="2x">
          <SimplifiedPriceColumn
            variant="maximal"
            currencyCode={currencyCode}
            locale={locale}
            price={price}
            priceInCredits={priceInCredits}
            shouldShowPriceInCredits={shouldShowPriceInCredits}
            showFromText={showFromText}
            unitAsSentence={unitAsSentence}
            priceTransparencyLevel={priceTransparencyLevel}
            isBHUser={isBHUser}
          />
          {cancellationPolicy && (
            <Text size="100" textColor="tertiary">
              {cancellationPolicy}
            </Text>
          )}
        </Flex>
      )}
      <Flex
        flexDirection="row"
        gap="4x"
        position="relative"
        zIndex={SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE.toString()}
      >
        <Button
          width="100%"
          flexBasis="0"
          flexGrow="1"
          size="small"
          href={profileUrl}
          webProps={{ hrefTarget }}
          onPress={() => {
            emitAnalyticsEvent(new SearchResultCardViewProfileClickEvent(analyticsProps));
          }}
        >
          {t`Full profile`}
        </Button>
        {showAddToShortlist ? (
          <AddToShortlistButton
            webProps={{ ref: shortlistButtonRef }}
            personOpk={opk}
            source="search"
            size="small"
            fullWidth
            flexBasis="0"
            flexGrow="1"
            removedText={t`Contact`}
            removedVariant="primary"
            addedText={t`Added`}
            onAdded={handleShortlistAdded}
          />
        ) : (
          <Button
            variant="primary"
            width="100%"
            flexBasis="0"
            flexGrow="1"
            size="small"
            href={contactUrl}
            webProps={{ hrefTarget }}
            onPress={() => {
              emitAnalyticsEvent(new SearchResultCardContactClickEvent(analyticsProps));
            }}
          >
            {t`Contact`}
          </Button>
        )}
      </Flex>
      {avatarFlight && <FlyingAvatar {...avatarFlight} onComplete={() => setAvatarFlight(null)} />}
    </Flex>
  );
};

export default MaximalCTABox;
